import React, { useEffect, useState } from 'react';
import { Button, Flex, message, Pagination, Popconfirm, Space, Table, Tag } from 'antd';
import type { TableColumnType } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { PageLink } from '@/config/pageConfig';

// 接口文档地址
// https://supptech.sg.larksuite.com/wiki/Nobsw0FhdilBpak9V2nlUc29gxh

export const CONTENT_REVIEW_IDS_KEY = 'content_review_ids';
export const CONTENT_REVIEW_INDEX_KEY = 'content_review_index';

// import { StatusEnm, StatusMap } from './type';
import { useRequest, useUpdateEffect } from 'ahooks';
import { cancel } from '@/api/recall';
import { PublishStatusMap, PublishStatusEnum } from './type';
import { postPushMessageRecordList, postPushMessageRecordSaveOrUpdate } from '@/api/noticeConfig';
import { PushMessageListReq, PushMessageListResp, PushMessageReq } from '@/types/noticeConfig';
import dayjs from 'dayjs';

export const ActivityStatusColorMap = {
  [PublishStatusEnum.SUCC]: 'green',
  [PublishStatusEnum.WAIT]: 'gray',
  [PublishStatusEnum.CANCEL]: 'red',
};

const NoticeConfig: React.FC = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data, loading, run, refresh } = useRequest(
    async (page: number = 1) => {
      const params: PushMessageListReq = {
        currentPage: page,
        pageSize,
      };
      const resp = await postPushMessageRecordList(params);

      if (resp.status === 200 && resp.data.status === 200) {
        return resp.data.data;
      }

      return {
        records: [],
        total: 0,
        currentPage: page,
        pageSize,
      };
    }
  );

  useUpdateEffect(() => {
    run(page);
  }, [page]);

  const onCancel = async (record: PushMessageListResp) => {
    const formData = new FormData();
    Object.keys(record).forEach((key) => {
      const value = record[key as keyof PushMessageListResp];
      // formData.append(key, value);
      if (key !== "pushTime" && key !== "pushStatus") {
        formData.append(key, value);
      }
    })
    const formattedTime = dayjs(record.pushTime).format(
      'YYYY-MM-DD HH:mm:ss'
    );
    formData.append('pushTime', formattedTime);
    formData.append('pushStatus', PublishStatusEnum.CANCEL);
    const res = await postPushMessageRecordSaveOrUpdate(formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (res.status !== 200 || res.data.status !== 200) {
      return message.error(res.data.message);
    }

    message.success(res.data.message);
    refresh();
  };

  const columns: TableColumnType<PushMessageListResp>[] = [
    {
      title: 'id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '推送标题',
      dataIndex: 'pushTitle',
      key: 'title',
    },
    {
      title: '推送状态',
      dataIndex: 'pushStatus',
      key: 'pushStatus',
      render: (_: PublishStatusEnum) => {
        return (
          <Tag color={ActivityStatusColorMap[_]}>
            {PublishStatusMap.get(_) as string}
          </Tag>
        );
      },
    },
    {
      title: '推送时间',
      dataIndex: 'pushTime',
      key: 'publishTime',
      render: (_: string) => {
        if (!_) return '-';
        return dayjs(_).format("YYYY-MM-DD HH:mm")
      },
    },
    {
      title: '操作人员',
      dataIndex: 'updateUser',
      key: 'publishUser',
      render: (_: string) => _ || '-',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.pushStatus === PublishStatusEnum.WAIT && (
            <Button
              type="link"
              onClick={() =>
                navigate(`${PageLink.NoticeConfig}/edit/${record.id}`)
              }
            >
              编辑
            </Button>
          )}
          <Button
            type="link"
            onClick={() =>
              navigate(`${PageLink.NoticeConfig}/detail/${record.id}`)
            }
          >
            查看详情
          </Button>
          {record.pushStatus === PublishStatusEnum.WAIT && (
            <Popconfirm
              title="取消发布确认"
              description="确定要取消发布吗？"
              onConfirm={() => onCancel(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger>
                取消发布
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="content-review-container">
      {contextHolder}
      <div className="table-container">
        <Space size="middle" className="table-header">
          <Button
            type="primary"
            onClick={() => navigate(`${PageLink.NoticeConfig}/create`)}
          >
            创建
          </Button>
        </Space>
        <Table
          size="small"
          loading={loading}
          rowKey="recallEmailId"
          key="recall"
          columns={columns}
          dataSource={data?.records || []}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>
    </div>
  );
};

export default NoticeConfig;
